import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import { SingleSalesFilterStatus } from '@spike/single-sale-model';
import { Period } from '@spike/model';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';
import { CalendarFilter } from 'components/UI';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { SelectionBar, SelectionBarOption } from 'components/UI/V3/SelectionBar';
import Spinner from 'components/UI/V3/Spinner';
import { FunctionComponent } from 'react';
import styles from './SalesHeader.module.scss';

type SalesStatusFilter = SingleSalesFilterStatus;

interface SalesHeaderProps {
    status: SalesStatusFilter;
    clientSearch: string;
    marketplaceIds: Array<number>;
    totalRecords?: number;
    loading: boolean;
    period: Period;
    optionSelected?: string;
    onChangeStatus: (status: SalesStatusFilter) => void;
    onChangeClientSearch: (value: string) => void;
    onChangeMarketplaceIds: (marketplaceIds: Array<number>) => void;
    onChangePeriod: (period: Period) => void;
    onChangeOptionSelected: (optionSelected?: string) => void;
}

export const SalesHeader: FunctionComponent<SalesHeaderProps> = ({
    status,
    clientSearch,
    marketplaceIds,
    totalRecords,
    loading,
    period,
    optionSelected,
    onChangeStatus,
    onChangeClientSearch,
    onChangeMarketplaceIds,
    onChangePeriod,
    onChangeOptionSelected
}) => {
    const getStatusLabel = (id: SingleSalesFilterStatus, label: string) =>
        loading && status === id ? (
            <span className={styles.tabLabel}>
                {label}
                <Spinner
                    size={14}
                    color="currentColor"
                />
            </span>
        ) : (
            label
        );

    const statusOptions: Array<SelectionBarOption<string>> = [
        { id: SingleSalesFilterStatus.Completed, name: getStatusLabel(SingleSalesFilterStatus.Completed, 'Completed') },
        { id: SingleSalesFilterStatus.Unpaid, name: getStatusLabel(SingleSalesFilterStatus.Unpaid, 'Unpaid') },
        {
            id: SingleSalesFilterStatus.Incomplete,
            name: getStatusLabel(SingleSalesFilterStatus.Incomplete, 'Incomplete')
        },
        {
            id: SingleSalesFilterStatus.All,
            name: getStatusLabel(SingleSalesFilterStatus.All, 'All'),
            selectedBackgroundColor: '#92b4a7'
        }
    ];

    const selectedTotalRecords = loading ? undefined : totalRecords;

    return (
        <header className={styles.header}>
            <h1 className={styles.title}>Sales</h1>

            <SelectionBar
                options={statusOptions}
                selectedId={status}
                selectedTotalRecords={selectedTotalRecords}
                onSelect={id => onChangeStatus(id as SalesStatusFilter)}
                className={styles.selectionBar}
            />

            <div className={styles.toolbar}>
                <SearchBar
                    searchText={clientSearch}
                    placeholder="Search by Client"
                    onChangeSearchText={onChangeClientSearch}
                    className={styles.search}
                />

                <div className={styles.actions}>
                    <OrganizationMarketplacesFilter
                        selected={marketplaceIds}
                        onChangeSelection={onChangeMarketplaceIds}
                        className={styles.locationsFilter}
                    />

                    <CalendarFilter
                        hideAllTime
                        disableFuture
                        hideUpcoming
                        period={period}
                        optionSelected={optionSelected}
                        buttonIcon={faCalendarDays}
                        className={styles.calendarFilter}
                        onChange={(nextPeriod, _allTime, nextOptionSelected) => {
                            onChangePeriod(nextPeriod);
                            onChangeOptionSelected(nextOptionSelected);
                        }}
                    />
                </div>
            </div>
        </header>
    );
};

export default SalesHeader;
